/**
 * Desktop input: WASD, the mouse, number keys, and the standard letters.
 *
 * The mouse scheme changed after the first people played it. It used to be:
 * left click to capture the pointer, then left click again to swing. That has
 * two problems. The pointer lock swallows the cursor, which is alarming the
 * first time and impossible to explain in a help screen; and it left the mouse
 * unable to do the two things people actually reach for it to do — go there,
 * and hit that.
 *
 * So now:
 *   left button  — click the world. The game works out whether that was ground
 *                  to walk to, something to fight, or somebody to talk to.
 *   right button — hold and move to turn the camera. No pointer lock, no
 *                  surprise, and it works the same in a browser test as it does
 *                  for a player.
 *
 * The keys come from the shared bindings list rather than being written out
 * again here, so the help screen and the actual behaviour cannot disagree.
 */

import { MOVEMENT_BINDINGS, actionKeyMap } from '@emberreach/shared';
import type { InputSource, InputState } from './index.js';

/* Built from the shared list, so a change there moves the key here too.
   The vectors stay local: "which way is forward" is this module's business. */
const MOVE_VECTORS: Record<string, [x: number, z: number]> = {
  KeyW: [0, 1], ArrowUp: [0, 1],
  KeyS: [0, -1], ArrowDown: [0, -1],
  KeyA: [-1, 0], ArrowLeft: [-1, 0],
  KeyD: [1, 0], ArrowRight: [1, 0]
};

const MOVE_CODES = new Set(
  MOVEMENT_BINDINGS.flatMap((b) => b.codes).filter((code) => code in MOVE_VECTORS)
);

const ACTION_KEYS = actionKeyMap();

export interface KeyboardOptions {
  /** Radians per pixel of mouse movement. */
  sensitivity: number;
  /** Invert vertical look. */
  invertY: boolean;
}

export function createKeyboardMouse(
  state: InputState,
  canvas: HTMLCanvasElement,
  options: KeyboardOptions
): InputSource {
  const held = new Set<string>();
  let turning = false;
  let lastX = 0;
  let lastY = 0;

  const recomputeMove = (): void => {
    let x = 0;
    let z = 0;
    for (const code of held) {
      const v = MOVE_VECTORS[code];
      if (v !== undefined) { x += v[0]; z += v[1]; }
    }
    state.moveX = Math.max(-1, Math.min(1, x));
    state.moveZ = Math.max(-1, Math.min(1, z));
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    // Never swallow keys aimed at a text field — otherwise typing "w" in the
    // chat box walks the character forward.
    const target = e.target as HTMLElement | null;
    if (target !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

    if (e.repeat) return;
    held.add(e.code);

    if (MOVE_CODES.has(e.code)) { recomputeMove(); e.preventDefault(); }
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') state.running = true;
    if (e.code === 'Space') { state.jumpQueued = true; e.preventDefault(); }

    const action = ACTION_KEYS[e.code];
    if (action !== undefined) { state.actions.add(action); e.preventDefault(); }
  };

  const onKeyUp = (e: KeyboardEvent): void => {
    held.delete(e.code);
    if (MOVE_CODES.has(e.code)) recomputeMove();
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') state.running = false;
  };

  /**
   * A window that loses focus never delivers keyup, so without this the player
   * keeps walking after alt-tab and the key appears stuck. The same goes for a
   * right button released outside the window.
   */
  const onBlur = (): void => {
    held.clear();
    state.moveX = 0;
    state.moveZ = 0;
    state.running = false;
    turning = false;
  };

  const onMouseMove = (e: MouseEvent): void => {
    if (!turning) return;
    /* movementX is not used, deliberately: outside pointer lock it is unreliable
       across browsers, and the difference between successive client positions
       is exactly as good for a drag. */
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    state.lookYaw += dx * options.sensitivity;
    state.lookPitch += (options.invertY ? -dy : dy) * options.sensitivity;
  };

  const onMouseDown = (e: MouseEvent): void => {
    if (e.button === 2) {
      turning = true;
      lastX = e.clientX;
      lastY = e.clientY;
      e.preventDefault();
    }
  };

  const onMouseUp = (e: MouseEvent): void => {
    if (e.button === 2) turning = false;
  };

  const onClick = (e: MouseEvent): void => {
    if (e.button !== 0) return;
    const rect = canvas.getBoundingClientRect();
    state.worldClicks.push({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  /* Right-drag turns the camera, so the context menu has to go — but only when
     the drag actually happened. A plain right click with no movement still gets
     suppressed, because a menu over a game canvas is never wanted. */
  const onContextMenu = (e: Event): void => e.preventDefault();

  const onWheel = (e: WheelEvent): void => {
    // preventDefault stops the page scrolling underneath the canvas, which on a
    // laptop trackpad is otherwise the usual result of trying to zoom.
    e.preventDefault();
    state.zoomDelta += e.deltaY;
  };

  return {
    attach() {
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('keyup', onKeyUp);
      window.addEventListener('blur', onBlur);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      canvas.addEventListener('mousedown', onMouseDown);
      canvas.addEventListener('click', onClick);
      canvas.addEventListener('contextmenu', onContextMenu);
      canvas.addEventListener('wheel', onWheel, { passive: false });
    },
    detach() {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('wheel', onWheel);
      if (document.pointerLockElement === canvas) document.exitPointerLock();
    }
  };
}
